import { Base } from 'components/Containers/BaseContainer';
import {
  Box,
  Divider,
  type SelectChangeEvent,
  Typography,
  Grid,
} from '@mui/material';
import React, { useState, useEffect, type FC } from 'react';
import { type MaintenanceSquares } from 'types/squares.type';
import { SquaresComposedChartComponent } from '../../../Helpers/Graphs/Squares.composedChart';
import { Selector } from 'components/Form/Selectors/Selector';
import {
  AddToReports,
  TypographyWithIcon,
} from '../../../Helpers/squareHelper';
import { FlexBox } from 'components/Containers/FlexBox';
import Colors from '../../../Helpers/Graphs/styles';
import CustomRangeDatePicker, {
  type SelectedDates,
} from '../../../Helpers/customDatePicker';
import { isDarkTheme } from 'utils/theme';
import { ThemePalette } from 'mui.theme';
import { useAppSelector } from 'store/hook';

interface PmVsCmExpenditureProps {
  barKeys: Array<{ name: string; color: string; label: string }>;
  graphHeader: string;
  enableDarkTheme: boolean;
  fetchSquaresData: (
    selectedStartDate: Date,
    selectedEndDate: Date
  ) => Promise<MaintenanceSquares[]>;
}

export const PmVsCmExpenditure: FC<PmVsCmExpenditureProps> = ({
  fetchSquaresData,
  barKeys,
  graphHeader,
  enableDarkTheme,
}) => {
  const [chartData, setChartData] = useState<MaintenanceSquares[]>([]);
  const [isLoading, setIsLoading] = useState(true); // Add isLoading state
  const [isNoData, setisNoData] = useState(false); // Add isLoading state

  const formerSelectedDates: any = sessionStorage.getItem(
    'datePickerSelection'
  );

  let initialStartDate = new Date();
  let initialEndDate = new Date();

  if (formerSelectedDates) {
    const dateObjs = JSON.parse(formerSelectedDates);

    initialStartDate = new Date(dateObjs.startDate);
    initialEndDate = new Date(dateObjs.endDate);
  }

  const [selectedDates, setSelectedDates] = useState<SelectedDates>({
    startDate: (() => {
      const date = initialStartDate;
      if (!formerSelectedDates) {
        date.setMonth(date.getMonth() - 110);
      }
      return date;
    })(),
    endDate: initialEndDate,
  });

  const handleDateChange = ({
    startDate,
    endDate,
  }: {
    startDate: Date | null;
    endDate: Date | null;
  }) => {
    setSelectedDates({ startDate, endDate });
    sessionStorage.setItem(
      'datePickerSelection',
      JSON.stringify({
        startDate,
        endDate,
      })
    );
  };

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true); // Set isLoading state to true
      setisNoData(false);

      if (selectedDates.startDate === null || selectedDates.endDate === null) {
        return;
      }
      const data = await fetchSquaresData(
        selectedDates.startDate,
        selectedDates.endDate
      );
      if (data.length > 0) {
        setChartData(data);
        setIsLoading(false); // Set isLoading state to false
      } else {
        setIsLoading(false); // Set isLoading state to false
        setisNoData(true);
      }
    }

    void fetchData();
  }, [selectedDates]);

  return (
    <>
      <Base>
        <Box
          sx={{
            backgroundColor: enableDarkTheme
              ? ThemePalette.dark.boxBackground
              : ThemePalette.light.boxBackground,
            paddingTop: '10px',
            paddingLeft: '10px',
            paddingRight: '10px',
            minWidth: '0px',
          }}
        >
          <Grid
            container
            spacing={0}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item sm={8} md={8}>
              <Typography
                sx={{
                  color: enableDarkTheme
                    ? ThemePalette.typography.white
                    : ThemePalette.typography.black,
                }}
              >
                PM/CM <b>Work Order {graphHeader} </b>For Date Range:(s)
              </Typography>
              <CustomRangeDatePicker
                selectedDates={selectedDates}
                handleDateChange={handleDateChange}
                enableDarkTheme={enableDarkTheme}
              />
            </Grid>
            <Grid item sm={4} md={2} justifyContent="flex-end">
              <AddToReports
                graphId={'PmVsCmExpenditure'}
                data={chartData}
                interval={selectedDates}
              />
            </Grid>
          </Grid>

          <Divider variant="middle" sx={{ margin: '3px' }} />

          <div style={{ width: '100%', height: '100%' }}>
            <div id="PmVsCmExpenditure">
              <SquaresComposedChartComponent
                data={chartData}
                barKeys={barKeys}
                xAxisDataKey="category"
                xAxisLabel="Maintenance"
                sort={''}
                isLoading={isLoading}
                isNoData={isNoData}
                yAxisLabel="Cost ($)"
                enableDarkTheme={enableDarkTheme}
              />
            </div>
          </div>
        </Box>
      </Base>
    </>
  );
};
